import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { getForm, createForm, updateForm, getSettings, sendFormEmail, getFormPdfPreview } from "@/lib/api";
import { saveAs } from 'file-saver';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Save,
  Send,
  FileText,
  Plus,
  Trash2,
  Loader2,
  Scan,
  CheckCircle2,
  Download,
  Eye,
} from "lucide-react";

const PRUEFER_OPTIONS = [
  { value: "Eugen Kauz", label: "Eugen Kauz" },
  { value: "Waldemar Kauz", label: "Waldemar Kauz" },
];

// Komponenten, die einen zusätzlichen 32-Zeichen-Scan-Eingang bekommen (gleiche TWINC-Logik)
const SCAN_ENABLED_COMPONENTS = [
  "SANZA-Applikator TWINC",
  "SANZA-Applikator Mat",
  "SANZA-Applikator Pad",
];

// Wandelt einen 32-Zeichen TWINC-Scan-Code in die Dezimal-Seriennummer um.
// Beispiel: 00007471D4D60540000A72E3B4990040 -> Bereich (Pos 19-27) = A72E3B499
//           gedreht zu 99B4E372A -> Hex zu Dezimal = 41260300074
const convertTwincScanCode = (raw) => {
  const code = (raw || "").trim().toUpperCase();
  if (code.length !== 32) {
    return { ok: false, error: "Code muss genau 32 Zeichen lang sein" };
  }
  if (!/^[0-9A-F]{32}$/.test(code)) {
    return { ok: false, error: "Code darf nur Hex-Zeichen (0-9, A-F) enthalten" };
  }
  const segment = code.slice(19, 28); // 9 Zeichen, z.B. "A72E3B499"
  const firstNibble = segment[0];
  const byteParts = [
    segment.slice(1, 3),
    segment.slice(3, 5),
    segment.slice(5, 7),
    segment.slice(7, 9),
  ];
  const reversedHex = byteParts.reverse().join("") + firstNibble;
  try {
    const decimal = BigInt("0x" + reversedHex).toString(10);
    return { ok: true, decimal };
  } catch (err) {
    return { ok: false, error: "Hex-zu-Dezimal-Umwandlung fehlgeschlagen" };
  }
};

const BF_PAKET_OPTIONS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "14", "15", "P"];
const BF_PAKET_DEFAULT_CHECKED = ["1", "2", "3", "4"];

const DEFAULT_COMPONENTS = [
  { name: "SANZA pro-Generator", serial_number: "", type: "serial", label: "Fabrikationsnummer", deduct_inventory: true },
  { name: "SANZA pro-Generator", serial_number: "", type: "serial", label: "MAC Nummer", deduct_inventory: false },
  { name: "Kabel EU", checked: true, type: "checkbox", label: "", exclude_from_pdf: true },
  { name: "Kabel UK", checked: false, type: "checkbox", label: "", exclude_from_pdf: true },
  { name: "Kabel US", checked: false, type: "checkbox", label: "", exclude_from_pdf: true },
  { name: "SANZA-Applikator Mat", serial_number: "", type: "rfid", label: "Fabrikationsnummer" },
  { name: "SANZA-Applikator Pad", serial_number: "", type: "rfid", label: "Fabrikationsnummer" },
  { name: "BF-Hand Elektroden", checked: false, type: "checkbox", label: "Fabrikationsnummer" },
  { name: "BF-Paket Set 1-", selected_packages: BF_PAKET_DEFAULT_CHECKED, type: "packages", label: "Fabrikationsnummer" },
  { name: "Scheiben-Applikatoren zu FP P", checked: false, type: "checkbox", label: "" },
  { name: "SANZA-Applikator TWINC", serial_number: "TWI", type: "serial", label: "Fabrikationsnummer" },
  { name: "SANZA -Applikator PEN", serial_number: "PEN", type: "serial", label: "Fabrikationsnummer" },
  { name: "LENOVO Tablet", serial_number: "", type: "serial", label: "Fabrikationsnummer", suffix: "und STAND" },
];

export default function FormEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [defaultRecipient, setDefaultRecipient] = useState("");
  const [scanErrors, setScanErrors] = useState({}); // { [componentIndex]: errorText }

  // Feste Werte (nicht änderbar)
  const FIXED_DATA = {
    product_type: "sanza pro",
    product_description: "Multifunktionales Magnetfeldgerät; Produktklasse Wellness",
    manufacturer: "Sanza International GmbH",
    manufacturer_address: "Europastraße 10 Top 4.1, A-5020 Salzburg",
    distributor: "Sanza International GmbH",
    distributor_address: "Europastraße 10 Top 4.1, A-5020 Salzburg",
    distributor_email: "info@sanza.eu",
    distributor_website: "www.sanzanet.com",
  };

  // Datum im Format DD.MM.YYYY formatieren
  const formatDate = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const [formData, setFormData] = useState({
    order_number: "",
    customer_name: "",
    packing_date: formatDate(new Date()),
    pdf_language: "DE",
    ...FIXED_DATA,
    components: DEFAULT_COMPONENTS,
    checked_by: "Eugen Kauz",
    checked_ok: false,
    status: "draft",
  });

  // RFID Detection
  const inputBuffer = useRef("");
  const inputTimeout = useRef(null);
  const activeInputRef = useRef(null);

  useEffect(() => {
    if (isEditing) {
      loadForm();
    }
    loadSettings();
  }, [id]);

  const loadForm = async () => {
    try {
      const response = await getForm(id);
      setFormData(response.data);
    } catch (error) {
      toast.error("Formular konnte nicht geladen werden");
      navigate("/");
    } finally {
      setIsLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await getSettings();
      if (response.data.default_recipient) {
        setDefaultRecipient(response.data.default_recipient);
        setRecipientEmail(response.data.default_recipient);
      }
    } catch (error) {
      console.error("Settings load error:", error);
    }
  };

  // RFID Keyboard Emulation Handler
  const handleRFIDInput = useCallback((e, componentIndex) => {
    // Clear previous timeout
    if (inputTimeout.current) {
      clearTimeout(inputTimeout.current);
    }

    // Add character to buffer
    inputBuffer.current += e.key;

    // Set timeout to process input
    inputTimeout.current = setTimeout(() => {
      const input = inputBuffer.current;
      
      // If input came fast (likely RFID), it was already captured in the input field
      // Clear buffer for next scan
      inputBuffer.current = "";
    }, 50);
  }, []);

  const updateComponent = (index, field, value) => {
    setFormData((prev) => {
      const newComponents = [...prev.components];
      const component = newComponents[index];
      
      // Für RFID-Felder (Mat und Pad): komplette gescannte Nummer speichern (max. 64 Zeichen)
      if (field === "serial_number" && component.type === "rfid") {
        value = value.slice(0, 64);
      }
      
      newComponents[index] = { ...component, [field]: value };
      return { ...prev, components: newComponents };
    });
  };

  // TWINC/Mat/Pad Scan Handler: speichert Rohscan + konvertiert bei 32 Zeichen automatisch zur Dezimal-SN
  const handleTwincScanChange = (componentIndex, value) => {
    const sanitized = value.toUpperCase();
    setFormData((prev) => {
      const newComponents = [...prev.components];
      newComponents[componentIndex] = { ...newComponents[componentIndex], raw_scan: sanitized };
      return { ...prev, components: newComponents };
    });

    if (sanitized.length === 32) {
      const result = convertTwincScanCode(sanitized);
      if (result.ok) {
        setFormData((prev) => {
          const newComponents = [...prev.components];
          newComponents[componentIndex] = {
            ...newComponents[componentIndex],
            serial_number: result.decimal,
            raw_scan: sanitized,
          };
          return { ...prev, components: newComponents };
        });
        setScanErrors((prev) => ({ ...prev, [componentIndex]: null }));
        toast.success(`Scan umgewandelt: ${result.decimal}`);
      } else {
        setScanErrors((prev) => ({ ...prev, [componentIndex]: result.error }));
        toast.error(result.error);
      }
    } else if (sanitized.length > 32) {
      setScanErrors((prev) => ({ ...prev, [componentIndex]: "Code zu lang (max. 32 Zeichen)" }));
    } else {
      setScanErrors((prev) => ({ ...prev, [componentIndex]: null }));
    }
  };

  const togglePackage = (componentIndex, packageValue) => {
    setFormData((prev) => {
      const newComponents = [...prev.components];
      const component = newComponents[componentIndex];
      const currentPackages = component.selected_packages || [];
      
      let newPackages;
      if (currentPackages.includes(packageValue)) {
        newPackages = currentPackages.filter((p) => p !== packageValue);
      } else {
        newPackages = [...currentPackages, packageValue];
      }
      
      newComponents[componentIndex] = {
        ...component,
        selected_packages: newPackages,
      };
      
      // Wenn Paket "P" ausgewählt wird, automatisch Scheiben-Applikatoren aktivieren
      if (packageValue === "P") {
        const scheibenIndex = newComponents.findIndex(c => c.name === "Scheiben-Applikatoren zu FP P");
        if (scheibenIndex !== -1) {
          newComponents[scheibenIndex] = {
            ...newComponents[scheibenIndex],
            checked: newPackages.includes("P"),
          };
        }
      }
      
      return { ...prev, components: newComponents };
    });
  };

  const addComponent = () => {
    setFormData((prev) => ({
      ...prev,
      components: [...prev.components, { name: "", serial_number: "", type: "serial" }],
    }));
  };

  const removeComponent = (index) => {
    setFormData((prev) => ({
      ...prev,
      components: prev.components.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async (status = "draft") => {
    if (!formData.order_number) {
      toast.error("Bitte geben Sie eine Auftragsnummer ein");
      return;
    }

    setIsSaving(true);
    try {
      const dataToSave = { ...formData, status };
      
      if (isEditing) {
        await updateForm(id, dataToSave);
        toast.success("Formular gespeichert");
      } else {
        const response = await createForm(dataToSave);
        toast.success("Formular erstellt");
        navigate(`/form/${response.data.id}`);
      }
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!id) {
      toast.error("Bitte speichern Sie das Formular zuerst");
      return;
    }

    try {
      const response = await getFormPdfPreview(id);
      setPdfPreview(response.data.pdf_base64);
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error("PDF-Vorschau konnte nicht geladen werden");
    }
  };

  const handleDownload = async () => {
    if (!id) {
      toast.error("Bitte speichern Sie das Formular zuerst");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forms/${id}/pdf`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      saveAs(blob, `${formData.order_number}.pdf`);
      toast.success("PDF heruntergeladen");
    } catch (error) {
      console.error('Download error:', error);
      toast.error("Download fehlgeschlagen");
    }
  };

  const handleSend = async () => {
    if (!recipientEmail) {
      toast.error("Bitte geben Sie eine E-Mail-Adresse ein");
      return;
    }

    setIsSending(true);
    try {
      await sendFormEmail(id, recipientEmail);
      toast.success("E-Mail erfolgreich gesendet");
      setShowSendDialog(false);
      loadForm(); // Reload to update status
    } catch (error) {
      toast.error(error.response?.data?.detail || "E-Mail konnte nicht gesendet werden");
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="form-editor-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isEditing ? `Auftrag ${formData.order_number}` : "Neues Formular"}
          </h1>
          <p className="text-slate-500 text-sm">
            {isEditing ? "Formular bearbeiten" : "Erstellen Sie eine neue Produktinformation"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isEditing && (
            <>
              <Button
                variant="outline"
                data-testid="preview-button"
                onClick={handlePreview}
                className="border-slate-200"
              >
                <Eye className="w-4 h-4 mr-2" />
                Vorschau
              </Button>
              <Button
                variant="outline"
                data-testid="download-button"
                onClick={handleDownload}
                className="border-slate-200"
              >
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
              <Button
                data-testid="send-button"
                onClick={() => setShowSendDialog(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Send className="w-4 h-4 mr-2" />
                Senden
              </Button>
            </>
          )}
          <Button
            data-testid="save-button"
            onClick={() => handleSave("draft")}
            disabled={isSaving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Speichern
          </Button>
        </div>
      </div>

      {/* Status Badge */}
      {isEditing && formData.status === "sent" && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-blue-600" />
          <p className="text-sm text-blue-800">
            Dieses Formular wurde am{" "}
            {formData.sent_at && new Date(formData.sent_at).toLocaleDateString("de-DE")} an{" "}
            <strong>{formData.sent_to}</strong> gesendet.
          </p>
        </div>
      )}

      <div className="max-w-4xl mx-auto space-y-3">
        {/* Main Form */}
        <div className="space-y-3">
          {/* Basic Info */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2 pt-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Auftragsdaten
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">PDF-Sprache:</Label>
                  <Select
                    value={formData.pdf_language || "DE"}
                    onValueChange={(value) => setFormData({ ...formData, pdf_language: value })}
                  >
                    <SelectTrigger data-testid="select-pdf-language" className="w-20 h-7 text-xs border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DE">DE</SelectItem>
                      <SelectItem value="EN">EN</SelectItem>
                      <SelectItem value="IT">IT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pb-3">
              <div className="flex items-center gap-3 mb-2">
                <Label htmlFor="order_number" className="text-sm whitespace-nowrap w-32">Auftragsnummer *</Label>
                <Input
                  id="order_number"
                  data-testid="input-order-number"
                  value={formData.order_number}
                  onChange={(e) => setFormData({ ...formData, order_number: e.target.value })}
                  placeholder="z.B. B25004488"
                  className="border-slate-200 h-8 flex-1"
                />
              </div>
              <div className="flex items-center gap-3">
                <Label htmlFor="customer_name" className="text-sm whitespace-nowrap w-32">Kundenname</Label>
                <Input
                  id="customer_name"
                  data-testid="input-customer-name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  placeholder="z.B. Max Mustermann"
                  className="border-slate-200 h-8 flex-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* Components / Serial Numbers */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Komponenten & Seriennummern
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 pb-3">
              {formData.components.map((component, index) => {
                // Skip Kabel components in main list (rendered separately)
                if (component.name.startsWith("Kabel ")) {
                  return null;
                }
                
                // Show Kabel group after MAC Nummer
                const showKabelGroup = component.label === "MAC Nummer";
                
                return (
                <div key={index}>
                <div
                  data-testid={`component-${index}`}
                  className="px-2 py-1.5 bg-slate-50 rounded border border-slate-100"
                >
                  {/* Checkbox Type - BF-Hand Elektroden, Scheiben-Applikatoren */}
                  {component.type === "checkbox" ? (
                    <div className="flex items-center justify-between py-1">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700">{component.name}</p>
                        {component.label && (
                          <p className="text-xs text-slate-500">{component.label}:</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`component-check-${index}`}
                          data-testid={`component-checkbox-${index}`}
                          checked={component.checked || false}
                          onCheckedChange={(checked) => updateComponent(index, "checked", checked)}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`clear-component-${index}`}
                          onClick={() => updateComponent(index, "checked", false)}
                          className="text-slate-400 hover:text-red-500 h-7 w-7"
                          title="Feld zurücksetzen"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ) : component.type === "packages" ? (
                    /* Packages Type - BF-Paket Set */
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-700">{component.name}</p>
                          <p className="text-xs text-slate-500">{component.label}:</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`clear-component-${index}`}
                          onClick={() => updateComponent(index, "selected_packages", [])}
                          className="text-slate-400 hover:text-red-500 h-7 w-7"
                          title="Auswahl zurücksetzen"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="space-y-1">
                        <div className="flex flex-wrap gap-2">
                          {BF_PAKET_OPTIONS.slice(0, 4).map((pkg) => (
                            <div key={pkg} className="flex items-center gap-1">
                              <Checkbox
                                id={`package-${index}-${pkg}`}
                                data-testid={`package-${index}-${pkg}`}
                                checked={(component.selected_packages || []).includes(pkg)}
                                onCheckedChange={() => togglePackage(index, pkg)}
                                className="h-4 w-4"
                              />
                              <Label 
                                htmlFor={`package-${index}-${pkg}`} 
                                className="text-xs text-slate-600 cursor-pointer"
                              >
                                {pkg}
                              </Label>
                            </div>
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {BF_PAKET_OPTIONS.slice(4).map((pkg) => (
                            <div key={pkg} className="flex items-center gap-1">
                              <Checkbox
                                id={`package-${index}-${pkg}`}
                                data-testid={`package-${index}-${pkg}`}
                                checked={(component.selected_packages || []).includes(pkg)}
                                onCheckedChange={() => togglePackage(index, pkg)}
                                className="h-4 w-4"
                              />
                              <Label 
                                htmlFor={`package-${index}-${pkg}`} 
                                className="text-xs text-slate-600 cursor-pointer"
                              >
                                {pkg}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Serial Number Type (regular or RFID) */
                    <>
                      {SCAN_ENABLED_COMPONENTS.includes(component.name) && (
                        <div className="px-2 py-1.5 bg-amber-50 rounded border border-amber-100 mb-1">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 grid sm:grid-cols-2 gap-2 items-center">
                              <div>
                                <p className="text-sm font-medium text-slate-700">
                                  Scan-Feld {component.name.replace("SANZA-Applikator ", "").replace("SANZA -Applikator ", "")}
                                </p>
                                <p className="text-xs text-slate-500">
                                  32-stelligen Hex-Code scannen — wird automatisch in Seriennummer umgewandelt
                                </p>
                              </div>
                              <div>
                                <Input
                                  data-testid={`twinc-scan-input-${index}`}
                                  value={component.raw_scan || ""}
                                  onChange={(e) => handleTwincScanChange(index, e.target.value)}
                                  placeholder="z.B. 00007471D4D60540000A72E3B4990040"
                                  className={`border-slate-200 bg-white font-mono text-xs h-8 ${
                                    scanErrors[index] ? "border-red-400 focus:ring-red-500" : "focus:ring-2 focus:ring-amber-500"
                                  }`}
                                  maxLength={32}
                                />
                                {scanErrors[index] && (
                                  <p
                                    data-testid={`twinc-scan-error-${index}`}
                                    className="text-[11px] text-red-600 mt-1"
                                  >
                                    {scanErrors[index]}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="w-7" />
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                      <div className="flex-1 grid sm:grid-cols-2 gap-2 items-center">
                        <div>
                          <p className="text-sm font-medium text-slate-700">{component.name}</p>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-slate-500">{component.label || "Fabrikationsnummer"}:</p>
                            {component.type === "rfid" && (
                              <Badge variant="secondary" className="text-[10px] px-1 py-0 bg-blue-50 text-blue-600">
                                <Scan className="w-2.5 h-2.5 mr-0.5" />
                                RFID
                              </Badge>
                            )}
                          </div>
                          {component.suffix && (
                            <p className="text-xs text-slate-600">{component.suffix}</p>
                          )}
                        </div>
                        <div>
                          <Input
                            data-testid={`component-serial-${index}`}
                            value={component.serial_number}
                            onChange={(e) => updateComponent(index, "serial_number", e.target.value)}
                            onKeyDown={(e) => handleRFIDInput(e, index)}
                            placeholder={component.type === "rfid" ? "RFID scannen..." : "Seriennummer"}
                            className={`border-slate-200 bg-white font-mono text-xs h-8 ${
                              component.type === "rfid" ? "focus:ring-2 focus:ring-blue-500 focus:border-blue-500" : ""
                            }`}
                          />
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`clear-component-${index}`}
                        onClick={() => updateComponent(index, "serial_number", component.name.includes("TWINC") ? "TWI" : component.name.includes("PEN") ? "PEN" : "")}
                        className="text-slate-400 hover:text-red-500 h-7 w-7"
                        title="Feld löschen"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                    </>
                  )}
                </div>
                
                {/* Kabel Group - shown after MAC Nummer */}
                {showKabelGroup && (
                  <div className="px-2 py-1.5 bg-blue-50 rounded border border-blue-100 mt-1">
                    <div className="flex items-center justify-between py-1">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700">Kabel</p>
                        <p className="text-xs text-slate-500">Typ auswählen:</p>
                      </div>
                      <div className="flex items-center gap-4">
                        {formData.components
                          .map((c, i) => ({ ...c, originalIndex: i }))
                          .filter(c => c.name.startsWith("Kabel "))
                          .map((kabel) => (
                            <div key={kabel.originalIndex} className="flex items-center gap-1">
                              <Checkbox
                                id={`kabel-${kabel.originalIndex}`}
                                checked={kabel.checked || false}
                                onCheckedChange={(checked) => {
                                  // Make exclusive: uncheck all other cables first
                                  const newComponents = formData.components.map((c, i) => {
                                    if (c.name.startsWith("Kabel ")) {
                                      return { ...c, checked: i === kabel.originalIndex ? checked : false };
                                    }
                                    return c;
                                  });
                                  setFormData({ ...formData, components: newComponents });
                                }}
                                className="h-4 w-4 rounded-full"
                              />
                              <Label 
                                htmlFor={`kabel-${kabel.originalIndex}`}
                                className="text-xs text-slate-600 cursor-pointer"
                              >
                                {kabel.name.replace("Kabel ", "")}
                              </Label>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
                </div>
              );
              })}
            </CardContent>
          </Card>

          {/* Prüfung & Unterschrift */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Prüfung & Freigabe
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 pb-3">
              <div className="flex items-center space-x-3 p-2 bg-slate-50 rounded border border-slate-200">
                <Checkbox
                  id="checked_ok"
                  data-testid="checkbox-checked-ok"
                  checked={formData.checked_ok}
                  onCheckedChange={(checked) => setFormData({ ...formData, checked_ok: checked })}
                />
                <Label 
                  htmlFor="checked_ok" 
                  className="text-sm font-medium text-slate-700 cursor-pointer"
                >
                  Die oben genannten Komponenten wurden geprüft - <span className="text-emerald-600 font-semibold">OK</span>
                </Label>
              </div>
              
              <div className="flex items-center gap-3">
                <Label htmlFor="checked_by" className="text-sm whitespace-nowrap">Geprüft von:</Label>
                <Select
                  value={formData.checked_by}
                  onValueChange={(value) => setFormData({ ...formData, checked_by: value })}
                >
                  <SelectTrigger data-testid="select-checked-by" className="border-slate-200 h-8 flex-1">
                    <SelectValue placeholder="Prüfer auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PRUEFER_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Send Email Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Formular per E-Mail senden</DialogTitle>
            <DialogDescription>
              Das PDF wird automatisch als Anhang versendet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="recipient">Empfänger E-Mail</Label>
              <Input
                id="recipient"
                data-testid="input-recipient-email"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="empfaenger@beispiel.de"
              />
              {defaultRecipient && recipientEmail !== defaultRecipient && (
                <Button
                  variant="link"
                  size="sm"
                  className="text-xs p-0 h-auto"
                  onClick={() => setRecipientEmail(defaultRecipient)}
                >
                  Standard-Empfänger verwenden: {defaultRecipient}
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>
              Abbrechen
            </Button>
            <Button
              data-testid="confirm-send-button"
              onClick={handleSend}
              disabled={isSending}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {isSending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>PDF-Vorschau</DialogTitle>
          </DialogHeader>
          <div className="bg-slate-200 p-4 rounded-lg">
            {pdfPreview && (
              <iframe
                src={`data:application/pdf;base64,${pdfPreview}`}
                className="w-full h-[600px] rounded bg-white"
                title="PDF Preview"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Schließen
            </Button>
            <Button onClick={handleDownload} className="bg-blue-600 hover:bg-blue-700">
              <Download className="w-4 h-4 mr-2" />
              Herunterladen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
